import React, { use, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
    View,
    StyleProp,
    ViewStyle,
    StyleSheet,
    NativeScrollEvent,
    NativeSyntheticEvent,
    ScrollViewProps,
    Platform,
    LayoutChangeEvent,
} from "react-native";
import commonStyles from "./common-style";
import { BlurView as ExpoBlurView } from "expo-blur";
import { LinearGradient } from "expo-linear-gradient";
import MaskedView from "@react-native-masked-view/masked-view";
import Button, { BUTTON_SHRINK_DURATION, ButtonMode, ButtonProps } from "../components/Button";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import VStack from "../ui_atoms/VStack";
import ScreenTitle from "../components/ScreenTitle";
import { RefreshControl } from "../components/RefreshControl/RefreshControl.web";
import { useRefresh } from "../hooks/refresh";
import { IconName } from "../components/Icon";
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withSequence,
    withTiming,
} from "react-native-reanimated";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import { ScreenHeaderManagerContext } from "../contexts/ScreenHeaderManager";
import { XOR } from "ts-essentials";
import {
    KeyboardAwareScrollView,
    KeyboardAwareScrollViewRef,
} from "react-native-keyboard-controller";
import Loading from "../components/Loading";
import { BottomTabBarHeightContext } from "@react-navigation/bottom-tabs";
import ScreenCardHeadband, { ScreenCardHeadbandProps } from "./ScreenCardHeadband";
import VirtualizedScreenBody from "./VirtualizedScreenBody";
import { LinearGradientConfig } from "../configs/linear-gradients";
import HStack from "../ui_atoms/HStack";
import Gap from "../ui_atoms/Gap";
import IconButton from "../components/IconButton";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { SelectChooser } from "../components/select/SelectChooser";
import { KeyboardStickyComponentManagerContext } from "../contexts/KeyboardStickyComponentManager";
import { useIntercom } from "../hooks/intercom";

const AnimatedView = Animated.createAnimatedComponent(View);

const BUTTON_BACKGROUND_BLUR_CONFIG = {
    0: "rgba(255,255,255,0)",
    0.2: "rgba(255,255,255,0.8)",
    0.8: "rgba(255,255,255,1)",
    1: "rgba(255,255,255,1)",
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const {
    locations: BUTTON_BACKGROUND_BLUR_LOCATIONS,
    colors: BUTTON_BACKGROUND_BLUR_COLORS,
}: LinearGradientConfig = Object.keys(BUTTON_BACKGROUND_BLUR_CONFIG)
    .sort((a, b) => Number(a) - Number(b))
    .reduce(
        (result, key) => ({
            locations: [...result.locations, Number(key)],
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            colors: [...result.colors, BUTTON_BACKGROUND_BLUR_CONFIG[key]],
        }),
        {
            locations: [] as number[],
            colors: [] as string[],
        },
    );

export type ButtonType = XOR<
    React.ReactNode,
    {
        title?: string;
        mode?: ButtonMode;
        isValid?: boolean;
        validating?: boolean;
    } & Pick<ButtonProps, "onPress" | "size" | "testID" | "style" | "textSize">
>;

export type Pagination = {
    isLoading: boolean;
    fetchMore: () => Promise<void>;
    hasNextPage: boolean;
};

export type ScreenCardOptionalAction = {
    icon?: IconName;
    onPress: () => void | Promise<void>;
    label: string;
    description?: string;
};

export type Props<T = unknown> = ScrollViewProps & {
    title?: string;
    isContentContainerFlex?: boolean;
    style?: StyleProp<ViewStyle>;
    contentStyle?: StyleProp<ViewStyle>;
    shrinkButtonToIcon?: IconName;
    slideButtonUp?: boolean;
    relativeActions?: boolean;
    refresh?: () => Promise<unknown>;
    headerComponent?: React.ReactNode;
    children?: React.ReactNode | ((screenActionsHeight: number) => React.ReactNode);
    pagination?: Pagination;
    headband?: ScreenCardHeadbandProps;
    noBottomBlur?: boolean;
    backgroundGradient?: LinearGradientConfig;
    optionalActions?: ScreenCardOptionalAction[];
    withAssistanceButton?: boolean;
    /**
     * Opt-in virtualization. When `virtualizedItems` and `renderItem` are provided,
     * the screen body is rendered by a `recyclerlistview`-backed virtualized list
     * (which becomes the scroll container) instead of the default ScrollView. In this
     * mode `children` is ignored and `headerComponent`/`title`/`headband` are pinned
     * above the list. Use it for long lists; small/static content should keep the
     * default ScrollView path.
     */
    virtualizedItems?: ReadonlyArray<T>;
    renderItem?: (item: T, index: number) => React.ReactElement;
    /** Extra content appended after the virtualized list (scrolls with it, like a list footer). */
    virtualizedFooter?: React.ReactNode;
} & XOR<{ buttons?: ButtonType[] | undefined | false }, { bottomComponent: React.ReactNode }>;

const BUTTON_SLIDEUP_DURATION = 400;

export type ScreenCardHandle = {
    scrollContentToEnd: () => void;
    scrollContentTo: (y: number, x?: number, animated?: boolean) => void;
    isScrolled: boolean;
};

const ScreenCard = <T = unknown,>({
    title,
    isContentContainerFlex,
    buttons,
    children,
    style,
    contentStyle,
    shrinkButtonToIcon,
    slideButtonUp,
    relativeActions,
    refresh,
    headerComponent,
    bottomComponent,
    pagination,
    headband,
    noBottomBlur,
    backgroundGradient,
    optionalActions: optionalActionsProp,
    withAssistanceButton = false,
    virtualizedItems,
    renderItem,
    virtualizedFooter,
    ref,
    ...props
}: Props<T> & { ref?: React.Ref<ScreenCardHandle> }) => {
    const i18n = useI18n();
    const { gridSize, marginSize, colors } = useTheme();
    const insets = useSafeAreaInsets();
    const isLarge = useIsScreenSizeMin("large");
    const { localRefresh, refreshing } = useRefresh({ refresh });
    const { setIsScreenScrolled } = use(ScreenHeaderManagerContext);
    const { hasStickyComponent } = use(KeyboardStickyComponentManagerContext);
    const tabBarHeight = use(BottomTabBarHeightContext);
    const { presentMessageComposer } = useIntercom();

    const optionalActions = useMemo<ScreenCardOptionalAction[]>(
        () => [
            ...(optionalActionsProp || []),
            ...(withAssistanceButton
                ? [
                      {
                          label: i18n.t("profile.contact_support"),
                          icon: "messages-question" as const,
                          mode: "outlined",
                          size: "large",
                          onPress: () => {
                              void presentMessageComposer();
                          },
                      },
                  ]
                : []),
        ],
        [i18n, optionalActionsProp, presentMessageComposer, withAssistanceButton],
    );

    const bottomInset = useMemo(() => {
        if (tabBarHeight !== undefined) {
            return gridSize;
        } else if (insets.bottom > 0) {
            return insets.bottom + gridSize / 2;
        } else {
            return gridSize;
        }
    }, [gridSize, insets.bottom, tabBarHeight]);

    const noButtons = useMemo(
        () =>
            (!bottomComponent && (buttons || []).length === 0) || (hasStickyComponent && !isLarge),
        [bottomComponent, buttons, hasStickyComponent, isLarge],
    );
    const [isScrolled, setIsScrolled] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const [nextPageLoadingHeight, setNextPageLoadingHeight] = useState(0);

    const blurOpacity = useSharedValue(1);

    const isPaginationLoading = useRef(false);

    const onScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            if (pagination?.hasNextPage && !isPaginationLoading.current) {
                const {
                    contentSize: { height: contentSizeHeight },
                    contentOffset: { y: contentOffsetY },
                    layoutMeasurement: { height: layoutHeight },
                } = e.nativeEvent;
                const scrollPosition = contentOffsetY + layoutHeight;

                if (scrollPosition > contentSizeHeight - nextPageLoadingHeight) {
                    isPaginationLoading.current = true;
                    void pagination.fetchMore().then(() => {
                        setTimeout(() => {
                            isPaginationLoading.current = false;
                        }, 0);
                    });
                }
            }

            props.onScroll?.(e);
            const _isScrolled = e.nativeEvent.contentOffset.y > 0;
            const isAtBottom =
                e.nativeEvent.contentOffset.y + e.nativeEvent.layoutMeasurement.height >=
                e.nativeEvent.contentSize.height - 10;
            setIsAtBottom(isAtBottom);
            setIsScrolled(_isScrolled);
            setIsScreenScrolled(_isScrolled);

            if (slideButtonUp) {
                blurOpacity.value = withTiming(_isScrolled ? 0 : 1, {
                    duration: BUTTON_SHRINK_DURATION,
                });
            }
        },
        [blurOpacity, nextPageLoadingHeight, pagination, props, setIsScreenScrolled, slideButtonUp],
    );

    const isButtonShrinked = useMemo(
        () => isScrolled && !isLarge && !isAtBottom,
        [isLarge, isScrolled, isAtBottom],
    );

    // By default we hide the slideButtonUp way below until we can calculate its height for animation
    const translateY = useSharedValue(1000);

    const animatedScreenActionsStyles = useAnimatedStyle(() => ({
        transform: [{ translateY: translateY.value }],
    }));

    const animatedBlurStyle = useAnimatedStyle(() => ({
        opacity: blurOpacity.value,
    }));

    const [screenActionsHeight, setScreenActionsHeight] = useState(0);

    const onScreenBottomLayout = useCallback(
        (e: LayoutChangeEvent) => {
            const height = e.nativeEvent.layout.height;
            if (height) {
                setScreenActionsHeight(height + bottomInset);
                if (slideButtonUp && !noButtons) {
                    translateY.value = withSequence(
                        withTiming(height, {
                            duration: 0,
                        }),
                        withTiming(0, {
                            duration: BUTTON_SLIDEUP_DURATION,
                        }),
                    );
                }
            }
        },
        [bottomInset, noButtons, slideButtonUp, translateY],
    );

    const scrollViewRef = useRef<KeyboardAwareScrollViewRef>(null);

    const scrollContentToEnd = useCallback(() => {
        scrollViewRef.current?.scrollToEnd();
    }, []);

    const scrollContentTo = useCallback((y: number, x?: number, animated: boolean = true) => {
        scrollViewRef.current?.scrollTo({
            y,
            x,
            animated,
        });
    }, []);

    const isChildrenFunction = useMemo(() => typeof children === "function", [children]);

    useImperativeHandle(ref, () => ({ scrollContentToEnd, scrollContentTo, isScrolled }));

    if (optionalActions.length > 0 && shrinkButtonToIcon) {
        console.warn("Optional actions are not supported when shrinkButtonToIcon is enabled.");
    }

    const [isOptionalActionsVisible, setIsOptionalActionsVisible] = useState(false);

    const paddingBottom = useMemo(() => {
        // When noButtons is true, we want to add padding to avoid content being cut off by the bottom inset
        if (noButtons) {
            return bottomInset;
        }
        // With relativeActions the bar is in the layout flow (not absolute), so there is no
        // overlap to compensate for.
        if (relativeActions) {
            return undefined;
        }
        // When isChildrenFunction is true, the children are responsible for rendering the screen actions, so we don't add extra padding to avoid interfering with the provided layout
        if (!isChildrenFunction) {
            return screenActionsHeight;
        }
    }, [bottomInset, isChildrenFunction, noButtons, relativeActions, screenActionsHeight]);

    const onVirtualizedScroll = useCallback(
        (offsetY: number, atBottom: boolean) => {
            const _isScrolled = offsetY > 0;
            // Same scroll bookkeeping as the ScrollView path: the header scrolls away with
            // the list (as its first row), so updating `isScrolled` lets the dynamic header
            // button reappear sticky in the navigation header (see useDynamicHeaderButton).
            setIsAtBottom(atBottom);
            setIsScrolled(_isScrolled);
            setIsScreenScrolled(_isScrolled);
            if (slideButtonUp) {
                blurOpacity.value = withTiming(_isScrolled ? 0 : 1, {
                    duration: BUTTON_SHRINK_DURATION,
                });
            }
        },
        [blurOpacity, setIsScreenScrolled, slideButtonUp],
    );

    const virtualized =
        virtualizedItems != null && renderItem != null
            ? { items: virtualizedItems, renderItem }
            : null;

    const headbandNode = headband ? <ScreenCardHeadband {...headband} /> : null;
    const titleNode = title ? (
        <ScreenTitle
            style={{
                marginTop: 0,
                paddingHorizontal: marginSize,
                paddingVertical: gridSize / 2,
            }}
        >
            {title}
        </ScreenTitle>
    ) : null;

    // In virtualized mode the header (headband + dynamic action + title) becomes the list's
    // first scrolling row so it scrolls away like a regular ScrollView; otherwise it renders
    // inline at the top of the scroll content.
    const scrollingHeader =
        headbandNode || headerComponent || titleNode ? (
            <>
                {headbandNode}
                {headerComponent}
                {titleNode}
            </>
        ) : null;

    // Shared chrome: the screenContainer/screenContent wrapper is identical in both modes;
    // only the outer scroll container, the header placement and the innermost body differ.
    const screenInner = (
        <>
            {!virtualized ? (
                <>
                    {headbandNode}
                    {headerComponent}
                </>
            ) : null}
            <View style={[commonStyles.screenContainer]}>
                <View
                    style={[
                        commonStyles.screenContent,
                        // `screenContent` is flexGrow:1 (flexShrink:0 by default), so on web it
                        // grows to its content height and leaves the inner RecyclerListView
                        // scroller unbounded (no scroll). `flex: 1` lets it shrink to the
                        // available space so the list scrolls. iOS/Android are unaffected.
                        virtualized ? { flex: 1, justifyContent: "flex-start" } : null,
                        contentStyle,
                    ]}
                >
                    {!virtualized ? titleNode : null}
                    {virtualized ? (
                        <VirtualizedScreenBody
                            header={scrollingHeader}
                            items={virtualized.items}
                            renderItem={virtualized.renderItem}
                            refreshControl={
                                localRefresh ? (
                                    <RefreshControl
                                        onRefresh={localRefresh}
                                        refreshing={refreshing}
                                    />
                                ) : undefined
                            }
                            onScroll={onVirtualizedScroll}
                            onEndReached={
                                pagination?.hasNextPage
                                    ? () => void pagination.fetchMore()
                                    : undefined
                            }
                            footerHeight={paddingBottom ?? 0}
                            footerContent={
                                <>
                                    {pagination?.hasNextPage ? (
                                        <VStack v={0.5}>
                                            <Loading />
                                        </VStack>
                                    ) : null}
                                    {virtualizedFooter}
                                </>
                            }
                        />
                    ) : (
                        <>
                            {typeof children === "function"
                                ? children(relativeActions ? 0 : screenActionsHeight)
                                : children}
                            {pagination?.hasNextPage ? (
                                <VStack
                                    v={0.5}
                                    onLayout={(e) => {
                                        setNextPageLoadingHeight(e.nativeEvent.layout.height);
                                    }}
                                >
                                    <Loading />
                                </VStack>
                            ) : null}
                        </>
                    )}
                </View>
            </View>
        </>
    );

    return (
        <>
            {backgroundGradient ? (
                <LinearGradient {...backgroundGradient} style={StyleSheet.absoluteFill} />
            ) : null}
            {virtualized ? (
                <View style={[{ flex: 1 }, style]}>{screenInner}</View>
            ) : (
                <KeyboardAwareScrollView
                    ref={scrollViewRef}
                    bottomOffset={screenActionsHeight}
                    style={[{ flex: 1 }, style]}
                    contentContainerStyle={{
                        flexGrow: isContentContainerFlex ? 1 : undefined,
                        paddingBottom,
                    }}
                    keyboardShouldPersistTaps="handled"
                    automaticallyAdjustKeyboardInsets
                    scrollEventThrottle={0}
                    refreshControl={
                        localRefresh && (
                            <RefreshControl onRefresh={localRefresh} refreshing={refreshing} />
                        )
                    }
                    {...props}
                    onScroll={onScroll}
                >
                    {screenInner}
                </KeyboardAwareScrollView>
            )}
            {!noButtons && (
                <AnimatedView
                    style={[
                        commonStyles.screenActions,
                        relativeActions && { position: "relative" },
                        slideButtonUp && animatedScreenActionsStyles,
                    ]}
                    onLayout={onScreenBottomLayout}
                >
                    <VStack
                        style={[
                            {
                                width: "100%",
                                paddingHorizontal: gridSize,
                                paddingTop: gridSize,
                                paddingBottom: bottomInset,
                            },
                            shrinkButtonToIcon &&
                                !isLarge && {
                                    width: "auto",
                                    backgroundColor: "transparent",
                                    alignSelf: "stretch",
                                },
                        ]}
                    >
                        <AnimatedView style={[StyleSheet.absoluteFill, animatedBlurStyle]}>
                            {!noBottomBlur ? (
                                Platform.OS === "ios" ? (
                                    <MaskedView
                                        maskElement={
                                            <LinearGradient
                                                locations={BUTTON_BACKGROUND_BLUR_LOCATIONS}
                                                colors={BUTTON_BACKGROUND_BLUR_COLORS}
                                                style={StyleSheet.absoluteFill}
                                            />
                                        }
                                        style={StyleSheet.absoluteFill}
                                    >
                                        <ExpoBlurView
                                            intensity={10}
                                            style={StyleSheet.absoluteFill}
                                        />
                                    </MaskedView>
                                ) : (
                                    <LinearGradient
                                        locations={BUTTON_BACKGROUND_BLUR_LOCATIONS}
                                        colors={BUTTON_BACKGROUND_BLUR_COLORS}
                                        style={[StyleSheet.absoluteFill, { opacity: 0.8 }]}
                                    />
                                )
                            ) : null}
                        </AnimatedView>
                        {shrinkButtonToIcon ? (
                            bottomComponent ||
                            (buttons || []).map((button, index) => (
                                <Button
                                    key={index}
                                    buttonMode={
                                        button.mode || (index === 1 ? "outlined" : undefined)
                                    }
                                    style={[{ width: "100%" }, index > 0 && { marginTop: 0 }]}
                                    size="large"
                                    disabled={
                                        button.isValid === undefined ? false : !button.isValid
                                    }
                                    processing={button.validating}
                                    label={
                                        button.title ||
                                        i18n.t("screens.auth.screen_card.validation_button_title")
                                    }
                                    onPress={button.onPress}
                                    shrinkToIcon={shrinkButtonToIcon}
                                    shrinked={isButtonShrinked}
                                    {...button}
                                />
                            ))
                        ) : (
                            <HStack alignment="center">
                                <VStack gap={0.33} style={{ flex: 1 }}>
                                    {bottomComponent ||
                                        (buttons || []).map((button, index) =>
                                            React.isValidElement(button) ? (
                                                React.cloneElement(button)
                                            ) : (
                                                <Button
                                                    key={index}
                                                    buttonMode={
                                                        button.mode ||
                                                        (index === 1 ? "outlined" : undefined)
                                                    }
                                                    style={[
                                                        { alignSelf: "stretch" },
                                                        index > 0 && { marginTop: 0 },
                                                    ]}
                                                    size="large"
                                                    disabled={
                                                        button.isValid === undefined
                                                            ? false
                                                            : !button.isValid
                                                    }
                                                    processing={button.validating}
                                                    label={
                                                        button.title ||
                                                        i18n.t(
                                                            "screens.auth.screen_card.validation_button_title",
                                                        )
                                                    }
                                                    onPress={button.onPress}
                                                    shrinkToIcon={shrinkButtonToIcon}
                                                    shrinked={isButtonShrinked}
                                                    {...button}
                                                />
                                            ),
                                        )}
                                </VStack>
                                {optionalActions && optionalActions.length > 0 && (
                                    <>
                                        <Gap gap={0.5} />
                                        <IconButton
                                            {...(optionalActions.length === 1
                                                ? {
                                                      icon: optionalActions[0].icon || "menu",
                                                      onPress: () => {
                                                          void optionalActions[0].onPress();
                                                      },
                                                  }
                                                : {
                                                      icon: "menu",
                                                      onPress: () => {
                                                          setIsOptionalActionsVisible(true);
                                                      },
                                                  })}
                                            mode="outlined"
                                            size="large"
                                        />
                                        <SelectChooser
                                            visible={isOptionalActionsVisible}
                                            onDismiss={() => {
                                                setIsOptionalActionsVisible(false);
                                            }}
                                            selectable={false}
                                            searchItems={optionalActions.map((action) => ({
                                                description: action.label,
                                                descriptionColor: colors.secondary,
                                                rightActionMode: "none",
                                                label: "",
                                                value: "",
                                                onPress: action.onPress,
                                                tags: [action.description],
                                            }))}
                                        />
                                    </>
                                )}
                            </HStack>
                        )}
                    </VStack>
                </AnimatedView>
            )}
        </>
    );
};

export default ScreenCard;
